import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { pickErr } from "@/lib/api";

export default function Students() {
  const [list, setList] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [stuRes, schRes] = await Promise.all([
          api.get("/admin/students"),
          api.get("/schools"),
        ]);
        if (cancelled) return;
        setList(Array.isArray(stuRes.data) ? stuRes.data : []);
        setSchools(Array.isArray(schRes.data) ? schRes.data : []);
      } catch (err) {
        if (!cancelled) setError(pickErr(err, "Failed to load students"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((s) => {
      if (schoolFilter && s.school_id !== schoolFilter) return false;
      if (!q) return true;
      const hay = [
        s.name,
        s.roll_number,
        s.student_code,
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      return hay.some((v) => v.includes(q));
    });
  }, [list, search, schoolFilter]);

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 mb-1">Students</h2>
      <p className="text-sm text-slate-500 mb-6">
        Global student roster across all schools.
      </p>

      <section className="bg-white border border-slate-200 rounded-lg p-4 mb-4 grid sm:grid-cols-[1fr_auto] gap-3 items-end">
        <div>
          <label className="block text-xs text-slate-600 mb-1">
            Search by name
          </label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-students-search"
            placeholder="Type a student name…"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-600 mb-1">School</label>
          <select
            value={schoolFilter}
            onChange={(e) => setSchoolFilter(e.target.value)}
            data-testid="admin-students-school-filter"
            className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white min-w-[14rem]"
          >
            <option value="">All schools</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg">
        <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold text-slate-900">
          {filtered.length} of {list.length}
        </div>
        {loading ? (
          <p className="px-5 py-8 text-sm text-slate-500">Loading…</p>
        ) : error ? (
          <p
            data-testid="admin-students-load-error"
            className="px-5 py-8 text-sm text-red-600"
          >
            {error}
          </p>
        ) : filtered.length === 0 ? (
          <p className="px-5 py-8 text-sm text-slate-500">No students.</p>
        ) : (
          <table className="w-full text-sm" data-testid="admin-students-table">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-5 py-2 font-medium">Student Code</th>
                <th className="px-5 py-2 font-medium">Name</th>
                <th className="px-5 py-2 font-medium">Roll</th>
                <th className="px-5 py-2 font-medium">Class</th>
                <th className="px-5 py-2 font-medium">School</th>
                <th className="px-5 py-2 font-medium text-right">Assigned</th>
                <th className="px-5 py-2 font-medium text-right">Completed</th>
                <th className="px-5 py-2 font-medium">Created</th>
                <th className="px-5 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="px-5 py-2 text-slate-900 font-mono">
                    {s.student_code || "—"}
                  </td>
                  <td className="px-5 py-2 text-slate-900">{s.name}</td>
                  <td className="px-5 py-2 text-slate-700 font-mono">
                    {s.roll_number}
                  </td>
                  <td className="px-5 py-2 text-slate-700">{s.class_name}</td>
                  <td className="px-5 py-2 text-slate-700">{s.school_name}</td>
                  <td className="px-5 py-2 text-slate-700 text-right tabular-nums">
                    {s.assigned_count}
                  </td>
                  <td className="px-5 py-2 text-slate-700 text-right tabular-nums">
                    {s.completed_count}
                  </td>
                  <td className="px-5 py-2 text-slate-500 text-xs">
                    {s.created_at?.slice(0, 10) || "—"}
                  </td>
                  <td className="px-5 py-2 text-right">
                    <Link
                      to={`/admin/students/${s.id}`}
                      data-testid={`admin-students-view-${s.id}`}
                      className="text-xs font-medium text-indigo-700 hover:text-indigo-900"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
